/* @img-migrated */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Send,
  Eye,
  Check,
  X,
  Trash2,
  AlertCircle,
  Clock,
  CheckCircle2,
  XCircle,
  PlayCircle,
  FileQuestion,
  Flag,
  Calendar,
  Building2,
  User as UserIcon,
  Download,
  ExternalLink,
  RefreshCw,
  ChevronDown,
  Paperclip,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

import Img from '../../components/Img';
import { resolveImg } from '../../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const resolveUrl = (url) =>
  resolveImg(url);

// ---------- Status config (new spec + legacy mappings) ----------
const STATUS_META = {
  pending: { label: 'Pending Review', tone: 'bg-amber-100 text-amber-800', icon: Clock },
  pending_review: { label: 'Pending Review', tone: 'bg-amber-100 text-amber-800', icon: Clock },
  needs_info: { label: 'Needs More Info', tone: 'bg-sky-100 text-sky-800', icon: FileQuestion },
  approved: { label: 'Approved', tone: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
  in_progress: { label: 'In Progress', tone: 'bg-indigo-100 text-indigo-800', icon: PlayCircle },
  completed: { label: 'Completed', tone: 'bg-slate-900 text-white', icon: CheckCircle2 },
  declined: { label: 'Declined', tone: 'bg-rose-100 text-rose-800', icon: XCircle },
  rejected: { label: 'Declined', tone: 'bg-rose-100 text-rose-800', icon: XCircle },
};

const STATUS_OPTIONS = [
  { value: 'pending_review', label: 'Pending Review' },
  { value: 'needs_info', label: 'Needs More Info' },
  { value: 'approved', label: 'Approved' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'declined', label: 'Declined' },
];

const PRIORITY_META = {
  urgent: { label: 'Urgent', tone: 'bg-rose-600 text-white' },
  high: { label: 'High', tone: 'bg-amber-500 text-white' },
  standard: { label: 'Standard', tone: 'bg-slate-100 text-slate-700' },
};

// --- Field grouping for structured detail view (version pro) ---
const SECTION_GROUPS = [
  {
    title: 'Client / Business Info',
    fields: [
      ['Company', 'company_name'],
      ['Contact', 'contact_name'],
      ['Email', 'email'],
      ['Phone / WhatsApp', 'phone_whatsapp'],
      ['Social Handles', 'social_handles'],
      ['Website', 'website'],
    ],
  },
  {
    title: 'Project Type',
    fields: [
      ['Types', 'project_types'],
      ['Other', 'project_type_other'],
    ],
  },
  {
    title: 'Objective',
    fields: [
      ['Goal', 'objective'],
      ['Desired Action', 'desired_action'],
      ['Target Audience', 'target_audience'],
    ],
  },
  {
    title: 'Content Details',
    fields: [
      ['Title', 'content_title'],
      ['Subtitle', 'content_subtitle'],
      ['Caption / Full Text', 'content_text'],
      ['Event Date', 'event_date'],
      ['Event Time', 'event_time'],
      ['Event Location', 'event_location'],
      ['Price', 'price'],
      ['Promotions', 'promotions'],
    ],
  },
  {
    title: 'Brand & Identity',
    fields: [
      ['Has Logo', 'has_logo'],
      ['Logo Files', 'logo_files'],
      ['Brand Colors', 'brand_colors'],
      ['Typography', 'typography'],
      ['Brand Guidelines', 'brand_guidelines'],
    ],
  },
  {
    title: 'References',
    fields: [
      ['Links', 'reference_links'],
      ['Reference Images', 'reference_images'],
      ['Style Description', 'style_description'],
      ['Competitors', 'competitors'],
    ],
  },
  {
    title: 'Style Direction',
    fields: [['Selected', 'style_directions']],
  },
  {
    title: 'Format Output',
    fields: [
      ['Formats', 'format_outputs'],
      ['Other', 'format_other'],
    ],
  },
  {
    title: 'Files / Material',
    fields: [['Attachments', 'material_files']],
  },
  {
    title: 'Deadline',
    fields: [
      ['Due Date', 'due_date'],
      ['Is Urgent', 'is_urgent'],
    ],
  },
  {
    title: 'Priority',
    fields: [['Priority Level', 'priority']],
  },
  {
    title: 'Final Notes',
    fields: [['Notes', 'final_notes']],
  },
];

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || { label: status, tone: 'bg-slate-100 text-slate-700', icon: AlertCircle };
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold ${meta.tone}`}>
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
};

const PriorityBadge = ({ priority }) => {
  if (!priority) return null;
  const meta = PRIORITY_META[priority] || { label: priority, tone: 'bg-slate-100 text-slate-700' };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${meta.tone}`}>
      <Flag className="w-3 h-3" />
      {meta.label}
    </span>
  );
};

// Attachment renderer — images show thumb, rest shows filename + download
const AttachmentList = ({ files = [] }) => {
  if (!files || files.length === 0) return <span className="text-[13px] text-slate-400">—</span>;
  return (
    <div className="flex flex-wrap gap-2">
      {files.map((f, i) => {
        const isImage = (f.content_type || '').startsWith('image/');
        return (
          <a
            key={`${f.url}-${i}`}
            href={resolveUrl(f.url)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 hover:border-[#002249]/50 hover:bg-slate-50 text-[12px] text-slate-700"
            data-testid={`admin-attachment-${i}`}
          >
            {isImage ? (
              <Img variant="generic"
                src={resolveUrl(f.url)}
                alt={f.filename}
                className="w-8 h-8 rounded object-cover"
                loading="lazy"
              />
            ) : (
              <Paperclip className="w-4 h-4 text-slate-400" />
            )}
            <span className="max-w-[160px] truncate">{f.filename || 'file'}</span>
            <Download className="w-3.5 h-3.5 opacity-60" />
          </a>
        );
      })}
    </div>
  );
};

const renderFieldValue = (key, value) => {
  if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
    return <span className="text-[13px] text-slate-400">—</span>;
  }
  if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
    return <AttachmentList files={value} />;
  }
  if (Array.isArray(value)) {
    return (
      <div className="flex flex-wrap gap-1.5">
        {value.map((v, i) => (
          <span key={i} className="px-2 py-0.5 rounded-full bg-slate-100 text-[12px] text-slate-700">
            {String(v)}
          </span>
        ))}
      </div>
    );
  }
  if (typeof value === 'boolean') {
    return <span className="text-[13px] font-medium">{value ? 'Yes' : 'No'}</span>;
  }
  if (key === 'reference_links' || (typeof value === 'string' && value.length > 200)) {
    return <p className="text-[13px] text-slate-700 whitespace-pre-wrap leading-relaxed">{value}</p>;
  }
  return <span className="text-[13px] text-slate-800 break-words">{String(value)}</span>;
};

const MarketingRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchRequests();
     
  }, [filter]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const res = await axios.get(`${API}/marketing-requests${params}`, { withCredentials: true });
      setRequests(res.data || []);
    } catch (e) {
      toast.error('Failed to load marketing requests');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (priorityFilter === 'all') return requests;
    return requests.filter((r) => (r.data?.priority || 'standard') === priorityFilter);
  }, [requests, priorityFilter]);

  const updateRequest = async (id, payload, successMessage = 'Updated') => {
    try {
      const res = await axios.put(`${API}/admin/marketing-requests/${id}`, payload, { withCredentials: true });
      toast.success(successMessage);
      setRequests((prev) => prev.map((r) => (r.id === id ? res.data : r)));
      setSelected((prev) => (prev && prev.id === id ? res.data : prev));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this marketing request? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/marketing-requests/${id}`, { withCredentials: true });
      toast.success('Request deleted');
      setRequests((prev) => prev.filter((r) => r.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch {
      toast.error('Failed to delete');
    }
  };

  const counts = useMemo(() => {
    const c = { all: requests.length };
    for (const r of requests) {
      const s = r.status || 'pending_review';
      const normalized = s === 'pending' ? 'pending_review' : s === 'rejected' ? 'declined' : s;
      c[normalized] = (c[normalized] || 0) + 1;
    }
    return c;
  }, [requests]);

  const toggleSection = (t) => setExpandedSections((p) => ({ ...p, [t]: !p[t] }));

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="marketing-requests-admin">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#002249]">Marketing Requests</h1>
          <p className="text-slate-500 mt-0.5 text-sm">
            {counts.all} total · review, assign, and manage the Eternity Media pipeline
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={fetchRequests}
            variant="outline"
            className="h-10 rounded-lg"
            data-testid="admin-refresh"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-4 sm:mb-5 overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
        {[
          { value: 'all', label: 'All', count: counts.all },
          { value: 'pending_review', label: 'Pending Review', count: counts.pending_review || 0 },
          { value: 'needs_info', label: 'Needs Info', count: counts.needs_info || 0 },
          { value: 'approved', label: 'Approved', count: counts.approved || 0 },
          { value: 'in_progress', label: 'In Progress', count: counts.in_progress || 0 },
          { value: 'completed', label: 'Completed', count: counts.completed || 0 },
          { value: 'declined', label: 'Declined', count: counts.declined || 0 },
        ].map((t) => (
          <button
            key={t.value}
            onClick={() => setFilter(t.value)}
            className={`flex items-center gap-2 px-3.5 py-2 rounded-full text-[13px] font-medium whitespace-nowrap transition-all border ${
              filter === t.value
                ? 'bg-[#002249] text-white border-[#002249]'
                : 'bg-white text-slate-600 border-slate-200 hover:border-[#002249]/50'
            }`}
            data-testid={`admin-tab-${t.value}`}
          >
            {t.label}
            <span
              className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                filter === t.value ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-700'
              }`}
            >
              {t.count}
            </span>
          </button>
        ))}

        <div className="ml-auto pl-2">
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="h-9 w-[160px] rounded-full text-[13px]" data-testid="admin-priority-filter">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priorities</SelectItem>
              <SelectItem value="urgent">Urgent</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="standard">Standard</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-[12px] font-semibold text-slate-600 uppercase tracking-wide">
                <th className="text-left px-4 py-3">Company</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Contact</th>
                <th className="text-left px-4 py-3 hidden lg:table-cell">Project</th>
                <th className="text-left px-4 py-3">Priority</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Deadline</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3 hidden xl:table-cell">Attachments</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filtered.map((r, i) => {
                const d = r.data || {};
                const company = d.company_name || d.business_name || d.event_name || '—';
                const contact = d.contact_name || r.submitted_by_name || '—';
                const contactEmail = d.email || r.submitted_by_email || '';
                const types = Array.isArray(d.project_types) ? d.project_types.join(', ') : (r.request_type || '—');
                const priority = d.priority || 'standard';
                const status = r.status || 'pending_review';
                const due = d.due_date || '';
                const attachCount =
                  (d.logo_files?.length || 0) +
                  (d.brand_guidelines?.length || 0) +
                  (d.reference_images?.length || 0) +
                  (d.material_files?.length || 0);
                return (
                  <tr key={r.id} className="hover:bg-slate-50/60" data-testid={`admin-row-${i}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-2 max-w-[240px]">
                        <div className="w-9 h-9 rounded-lg bg-[#002249]/10 text-[#002249] flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-[13px] font-semibold text-slate-900 truncate">{company}</p>
                          <p className="text-[11px] text-slate-500">
                            {new Date(r.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <p className="text-[13px] text-slate-800 truncate max-w-[160px]">{contact}</p>
                      <p className="text-[11px] text-slate-500 truncate max-w-[160px]">{contactEmail}</p>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <p className="text-[12px] text-slate-700 max-w-[220px] line-clamp-2">{types}</p>
                    </td>
                    <td className="px-4 py-3">
                      <PriorityBadge priority={priority} />
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      {due ? (
                        <span className="text-[12px] text-slate-700 inline-flex items-center gap-1">
                          <Calendar className="w-3 h-3 text-slate-400" />
                          {due}
                        </span>
                      ) : (
                        <span className="text-[12px] text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={status} />
                    </td>
                    <td className="px-4 py-3 hidden xl:table-cell">
                      {attachCount > 0 ? (
                        <span className="inline-flex items-center gap-1 text-[12px] text-slate-700 bg-slate-100 px-2 py-0.5 rounded-full">
                          <Paperclip className="w-3 h-3" />
                          {attachCount}
                        </span>
                      ) : (
                        <span className="text-[12px] text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelected(r);
                            setExpandedSections({});
                          }}
                          className="h-8 w-8 p-0"
                          data-testid={`admin-view-${i}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(r.id)}
                          className="h-8 w-8 p-0 text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                          data-testid={`admin-delete-${i}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="p-12 text-center text-slate-500">
              <Send className="w-14 h-14 mx-auto mb-3 text-slate-300" />
              <p className="text-sm">No marketing requests match the current filter.</p>
            </div>
          )}
        </div>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#002249]">
              <Building2 className="w-5 h-5" />
              {selected?.data?.company_name || selected?.data?.business_name || 'Marketing Request'}
            </DialogTitle>
          </DialogHeader>

          {selected && (
            <div className="space-y-5 mt-3">
              {/* Top meta */}
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge status={selected.status || 'pending_review'} />
                <PriorityBadge priority={selected.data?.priority || 'standard'} />
                {selected.data?.is_urgent && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 text-[11px] font-semibold border border-rose-200">
                    <AlertCircle className="w-3 h-3" /> Urgent
                  </span>
                )}
                <span className="ml-auto text-[12px] text-slate-500">
                  Submitted {new Date(selected.created_at).toLocaleString()}
                </span>
              </div>

              {/* Quick actions row */}
              <div className="grid sm:grid-cols-3 gap-3 p-4 rounded-xl bg-slate-50 border border-slate-200">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Status</p>
                  <Select
                    value={selected.status || 'pending_review'}
                    onValueChange={(v) => updateRequest(selected.id, { status: v }, `Status → ${v.replace('_', ' ')}`)}
                  >
                    <SelectTrigger className="h-9 rounded-lg" data-testid="admin-update-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Assigned To</p>
                  <Input
                    defaultValue={selected.assigned_to || ''}
                    placeholder="Team member name / email"
                    className="h-9 rounded-lg text-[13px]"
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v !== (selected.assigned_to || '')) {
                        updateRequest(selected.id, { assigned_to: v }, v ? `Assigned to ${v}` : 'Assignment cleared');
                      }
                    }}
                    data-testid="admin-assigned-to"
                  />
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Deadline</p>
                  <div className="h-9 rounded-lg bg-white border border-slate-200 flex items-center gap-2 px-3 text-[13px] text-slate-700">
                    <Calendar className="w-3.5 h-3.5 text-slate-400" />
                    {selected.data?.due_date || 'Not set'}
                  </div>
                </div>
              </div>

              {/* Admin notes */}
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                  Internal Admin Notes
                </p>
                <Textarea
                  defaultValue={selected.admin_notes || ''}
                  rows={3}
                  placeholder="Internal notes (not visible to the client)..."
                  className="rounded-lg text-[13px]"
                  onBlur={(e) => {
                    const v = e.target.value;
                    if (v !== (selected.admin_notes || '')) {
                      updateRequest(selected.id, { admin_notes: v }, 'Notes saved');
                    }
                  }}
                  data-testid="admin-notes"
                />
              </div>

              {/* Submitter */}
              <div className="p-4 rounded-xl bg-white border border-slate-200">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">
                  Submitter
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#002249]/10 flex items-center justify-center text-[#002249]">
                    <UserIcon className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[13px] font-semibold text-slate-900">
                      {selected.submitted_by_name || selected.data?.contact_name || '—'}
                    </p>
                    <p className="text-[12px] text-slate-500 truncate">
                      {selected.submitted_by_email || selected.data?.email || '—'}
                    </p>
                  </div>
                  {(selected.data?.website) && (
                    <a
                      href={selected.data.website.startsWith('http') ? selected.data.website : `https://${selected.data.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-auto inline-flex items-center gap-1 text-[12px] text-[#002249] hover:underline"
                    >
                      Website <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>

              {/* 12 sections — expandable */}
              <div className="space-y-2">
                {SECTION_GROUPS.map((group) => {
                  const d = selected.data || {};
                  // Determine if group has any value
                  const hasData = group.fields.some(([_, k]) => {
                    const v = d[k];
                    if (Array.isArray(v)) return v.length > 0;
                    if (typeof v === 'boolean') return v;
                    return v !== undefined && v !== null && v !== '';
                  });
                  const open = expandedSections[group.title] ?? hasData;
                  return (
                    <div key={group.title} className="rounded-xl border border-slate-200 bg-white">
                      <button
                        type="button"
                        onClick={() => toggleSection(group.title)}
                        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50 rounded-xl"
                        data-testid={`admin-section-${group.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      >
                        <span className="text-[13px] font-semibold text-[#002249]">
                          {group.title}
                          {!hasData && <span className="ml-2 text-[11px] font-normal text-slate-400">(empty)</span>}
                        </span>
                        <ChevronDown
                          className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
                        />
                      </button>
                      {open && (
                        <div className="px-4 pb-4 pt-1 space-y-3 border-t border-slate-100">
                          {group.fields.map(([label, key]) => (
                            <div key={key} className="grid sm:grid-cols-[180px_1fr] gap-1 sm:gap-3">
                              <span className="text-[11px] uppercase tracking-wide text-slate-500 pt-0.5">
                                {label}
                              </span>
                              <div>{renderFieldValue(key, d[key])}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Bottom actions */}
              <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-200">
                <Button
                  onClick={() => updateRequest(selected.id, { status: 'approved' }, 'Approved')}
                  className="bg-emerald-600 hover:bg-emerald-700 rounded-lg h-9 text-[13px]"
                  data-testid="admin-action-approve"
                >
                  <Check className="w-4 h-4 mr-1.5" /> Approve
                </Button>
                <Button
                  onClick={() => updateRequest(selected.id, { status: 'needs_info' }, 'Requested more info')}
                  variant="outline"
                  className="border-sky-500 text-sky-700 hover:bg-sky-50 rounded-lg h-9 text-[13px]"
                  data-testid="admin-action-needs-info"
                >
                  <FileQuestion className="w-4 h-4 mr-1.5" /> Request More Info
                </Button>
                <Button
                  onClick={() => updateRequest(selected.id, { status: 'in_progress' }, 'Moved to In Progress')}
                  variant="outline"
                  className="border-indigo-500 text-indigo-700 hover:bg-indigo-50 rounded-lg h-9 text-[13px]"
                  data-testid="admin-action-in-progress"
                >
                  <PlayCircle className="w-4 h-4 mr-1.5" /> Start Work
                </Button>
                <Button
                  onClick={() => updateRequest(selected.id, { status: 'completed' }, 'Marked completed')}
                  variant="outline"
                  className="border-slate-800 text-slate-800 hover:bg-slate-100 rounded-lg h-9 text-[13px]"
                  data-testid="admin-action-completed"
                >
                  <CheckCircle2 className="w-4 h-4 mr-1.5" /> Mark Completed
                </Button>
                <Button
                  onClick={() => updateRequest(selected.id, { status: 'declined' }, 'Declined')}
                  variant="outline"
                  className="border-rose-500 text-rose-600 hover:bg-rose-50 rounded-lg h-9 text-[13px]"
                  data-testid="admin-action-decline"
                >
                  <X className="w-4 h-4 mr-1.5" /> Decline
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MarketingRequestsPage;
